/**
 * CommunityMap — interactive Google Map showing pins for each address linked
 * to the selected community, plus optional "pending pins" representing
 * addresses staged to be added in bulk (P2.7b Slice 3).
 *
 * Uses @vis.gl/react-google-maps (playbook-recommended package).
 *
 * Geocoding: each saved address' `place_id` is resolved to lat/lng via the
 * Geocoding API after the Maps JS script loads. Pending pins already carry
 * lat/lng (they came from a map click) so no extra geocode is needed for them.
 *
 * Map clicks (Slice 3): when `onPendingPinAdd` is supplied, every click is
 * reverse-geocoded inside the map (via `useMapsLibrary('geocoding')`) to
 * find the closest place_id + formatted_address, then bubbled up.
 *
 * mapId: falls back to Google's DEMO_MAP_ID in development so AdvancedMarker
 * renders without needing a Cloud-Console-configured style. Set
 * REACT_APP_GOOGLE_MAPS_MAP_ID in production to a real Cloud mapId.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary, ControlPosition } from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from '@/config/constants';
import { loadGoogleMapsLibraries } from '@/utils/googleMapsLoader';
import { isDesktopOffline } from '@/api/desktopSync';

const LAS_VEGAS = { lat: 36.1699, lng: -115.1398 };
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID
  || (process.env.NODE_ENV === 'production' ? undefined : 'DEMO_MAP_ID');
const MAP_HEIGHT = 180;
const validCoordinates = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng)
  && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

// ---------------------------------------------------------------------------
// MarkerLayer: renders saved-address pins (green) + pending pins (amber).
// Runs inside <Map> so useMap() and useMapsLibrary() work.
// ---------------------------------------------------------------------------
function MarkerLayer({ addresses, pendingPins, onSavedAddressRemove, onPendingPinRemove, fitKey, captureSelection, isCurrentSelection }) {
  const map = useMap();
  const geocodingLib = useMapsLibrary('geocoding');
  const [pins, setPins] = useState([]);
  const [lookupMessage, setLookupMessage] = useState('');
  const authority = useRef(null);
  authority.current = { captureSelection, isCurrentSelection };
  // Stable ref to the latest addresses so the geocode effect can read fresh
  // data without depending on the array's reference identity (which changes
  // every parent re-render, even when contents are unchanged).
  const addressesRef = useRef(addresses);
  useEffect(() => { addressesRef.current = addresses; }, [addresses]);

  // Stable signature: only changes when the actual set of saved place_ids
  // changes (community switch, bulk save, or right-click removal). New
  // pending pins do NOT change this, so the geocoder won't re-run and the
  // map won't auto-fit when the user is staging.
  const addressSignature = useMemo(
    () => addresses.map((a) => a.place_id).filter(Boolean).sort().join('|'),
    [addresses],
  );

  useEffect(() => {
    const viable = addressesRef.current.filter((a) => a.place_id);
    if (!viable.length) { setPins([]); return; }

    // Addresses with stored coords skip geocoding entirely.
    const preresolved = viable.filter((a) => validCoordinates(a.lat, a.lng));
    const needsGeocode = viable.filter((a) => !validCoordinates(a.lat, a.lng));

    const preresolvedPins = preresolved.map((addr) => ({
      key: addr.place_id,
      lat: addr.lat,
      lng: addr.lng,
      label: addr.formatted_address || addr.place_id,
    }));
    setPins(preresolvedPins);

    if (needsGeocode.length === 0) {
      setPins(preresolvedPins);
      return;
    }

    // Fall back to geocoding for any addresses missing stored coords.
    if (!geocodingLib) return;
    const geocoder = new geocodingLib.Geocoder();
    let active = true;
    const initiated = authority.current;
    const ticket = initiated.captureSelection?.();
    const owned = () => active && !isDesktopOffline()
      && (!initiated.isCurrentSelection || initiated.isCurrentSelection(ticket));
    if (!owned()) return;
    let remaining = needsGeocode.length;
    const geocodedPins = [];
    const timer = setTimeout(() => {
      if (owned()) setLookupMessage('Some saved addresses could not be located on the map. Their list entries are preserved.');
      active = false;
    }, 15000);

    needsGeocode.forEach((addr) => {
      const completed = (results, status) => {
        if (!owned()) return;
        if (status === 'OK' && results?.[0]) {
          const loc = results[0].geometry?.location;
          const lat = loc?.lat?.(); const lng = loc?.lng?.();
          if (validCoordinates(lat, lng)) geocodedPins.push({
            key: addr.place_id,
            lat,
            lng,
            label: addr.formatted_address || addr.place_id,
          });
        }
        remaining -= 1;
        setPins([...preresolvedPins, ...geocodedPins]);
        if (remaining === 0) {
          clearTimeout(timer);
          if (geocodedPins.length < needsGeocode.length) setLookupMessage('Some saved addresses could not be located on the map. Their list entries are preserved.');
        }
      };
      try { geocoder.geocode({ placeId: addr.place_id }, completed); }
      catch { completed([], 'ERROR'); }
    });
    return () => { active = false; clearTimeout(timer); };
  }, [geocodingLib, addressSignature]);

  // Auto-fit bounds — fires on first resolution of a given saved-pin set
  // (community switch or bulk save), OR whenever fitKey increments (manual
  // "center" request from the detail panel). Stays put while staging.
  const lastFitRef = useRef({ signature: '', fitKey: undefined });
  const pinSignature = useMemo(
    () => pins.map((p) => p.key).sort().join('|'),
    [pins],
  );

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (!pins.length) return;
    const alreadyFitted =
      pinSignature === lastFitRef.current.signature &&
      fitKey === lastFitRef.current.fitKey;
    if (alreadyFitted) return;
    lastFitRef.current = { signature: pinSignature, fitKey };

    if (pins.length === 1) {
      map.setCenter({ lat: pins[0].lat, lng: pins[0].lng });
      map.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 48);
  }, [map, pinSignature, pins, fitKey]);

  return (
    <>
      {lookupMessage && <div role="status" className="absolute bottom-10 left-3 bg-white p-2 text-xs rounded shadow">{lookupMessage}</div>}
      {pins.map((p) => (
        <AdvancedMarker
          key={p.key}
          position={{ lat: p.lat, lng: p.lng }}
          title={`${p.label}${onSavedAddressRemove ? ' — right-click to remove' : ''}`}
        >
          <div
            className={`w-4 h-4 rounded-full bg-green-400 border-2 border-stone-800 shadow ${onSavedAddressRemove ? 'cursor-context-menu' : ''}`}
            onContextMenu={(e) => {
              if (!onSavedAddressRemove) return;
              e.preventDefault();
              e.stopPropagation();
              onSavedAddressRemove(p.key);
            }}
            data-testid={`community-map-saved-pin-${p.key}`}
          />
        </AdvancedMarker>
      ))}
      {pendingPins
        .filter((p) => validCoordinates(p.lat, p.lng))
        .map((p) => (
          <AdvancedMarker
            key={`pending-${p.tempId}`}
            position={{ lat: p.lat, lng: p.lng }}
            title={`${p.formatted_address || 'Pending pin'}${onPendingPinRemove ? ' — right-click to remove' : ''}`}
          >
            <div
              className={`w-4 h-4 rounded-full bg-amber-500 border-2 border-stone-800 shadow animate-pulse ${onPendingPinRemove ? 'cursor-context-menu' : ''}`}
              onContextMenu={(e) => {
                if (!onPendingPinRemove) return;
                e.preventDefault();
                e.stopPropagation();
                onPendingPinRemove(p.tempId);
              }}
              data-testid={`community-map-pending-pin-${p.tempId}`}
            />
          </AdvancedMarker>
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// ClickHandler: turns map clicks into reverse-geocoded pending-pin candidates.
// Lives inside <Map> so it can use useMapsLibrary('geocoding').
// ---------------------------------------------------------------------------
function ClickHandler({ onPendingPinAdd, existingPlaceIds, captureSelection, isCurrentSelection }) {
  const map = useMap();
  const geocodingLib = useMapsLibrary('geocoding');
  const latest = useRef(null);
  latest.current = { onPendingPinAdd, existingPlaceIds, captureSelection, isCurrentSelection };
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!map || !geocodingLib || !window.google?.maps) return;
    const geocoder = new geocodingLib.Geocoder();
    let active = true;
    const timers = new Set();
    const listener = map.addListener('click', (e) => {
      const initiated = latest.current;
      if (!initiated.onPendingPinAdd || isDesktopOffline()) return;
      const ticket = initiated.captureSelection?.();
      const owned = () => active && !isDesktopOffline()
        && (!initiated.isCurrentSelection || initiated.isCurrentSelection(ticket));
      if (!owned()) return;
      const lat = e.latLng?.lat?.();
      const lng = e.latLng?.lng?.();
      if (!validCoordinates(lat, lng)) return;
      let settled = false;
      setMessage('');
      const timer = setTimeout(() => {
        settled = true; timers.delete(timer);
        if (owned()) setMessage('Address lookup timed out. Click the building again to retry.');
      }, 15000);
      timers.add(timer);
      const finish = () => { clearTimeout(timer); timers.delete(timer); settled = true; };
      try { geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (settled) return;
        finish();
        if (!owned()) return;
        if (status !== 'OK' || !results?.length) {
          setMessage('No address could be resolved. Try another point or use address search.');
          return;
        }
        // Prefer the first street_address / premise result for a building-level pin.
        const preferred = results.find((r) =>
          r.types?.some((t) => ['street_address', 'premise', 'subpremise'].includes(t))
        ) || results[0];
        const place_id = preferred.place_id;
        const formatted_address = preferred.formatted_address;
        if (!place_id) return;
        // Skip if already saved on the community
        if (latest.current.existingPlaceIds?.has(place_id)) return;
        const pin = {
          place_id,
          formatted_address,
          lat,
          lng,
        };
        if (initiated.captureSelection) initiated.onPendingPinAdd(pin, ticket);
        else initiated.onPendingPinAdd(pin);
      }); } catch {
        finish();
        if (owned()) setMessage('Address lookup is unavailable. Try another point or use address search.');
      }
    });
    return () => {
      active = false;
      timers.forEach(clearTimeout); timers.clear();
      if (listener) window.google.maps.event.removeListener(listener);
    };
  }, [map, geocodingLib]);

  return message ? <div role="status" className="absolute bottom-3 left-3 bg-white p-2 text-xs rounded shadow">{message}</div> : null;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export default function CommunityMap({
  addresses = [],
  pendingPins = [],
  onPendingPinAdd,
  onPendingPinRemove,
  onSavedAddressRemove,
  fullHeight = false,
  fitKey,
  selectionKey = 'standalone',
  captureSelection,
  isCurrentSelection,
}) {
  const [loadState, setLoadState] = useState({ ready: false, error: null });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const loadPendingRef = useRef(false);
  const retryRequestedRef = useRef(false);
  const offline = isDesktopOffline();
  useEffect(() => {
    if (offline) { retryRequestedRef.current = false; return; }
    let current = true;
    loadPendingRef.current = true;
    setLoadState({ ready: false, error: null });
    const retry = retryRequestedRef.current;
    retryRequestedRef.current = false;
    loadGoogleMapsLibraries(['core', 'maps', 'marker', 'geocoding'], { retry, requireImporter: true })
      .then(() => {
        if (!current || isDesktopOffline()) return;
        // Never let APIProvider bootstrap around a missing shared importer.
        if (typeof window.google?.maps?.importLibrary !== 'function') {
          throw new Error('Google Maps is not ready for the community map. Your work is preserved.');
        }
        setLoadState({ ready: true, error: null });
      })
      .catch(error => { if (current) setLoadState({ ready: false, error }); })
      .finally(() => { if (current) loadPendingRef.current = false; });
    return () => { current = false; };
  }, [offline, loadAttempt]);
  const retryLoading = () => {
    if (offline || loadPendingRef.current || !loadState.error?.retryable) return;
    loadPendingRef.current = true;
    retryRequestedRef.current = true;
    setLoadState({ ready: false, error: null });
    setLoadAttempt(attempt => attempt + 1);
  };
  const containerStyle = fullHeight ? { width: '100%', height: '100%' } : { height: MAP_HEIGHT };
  const containerClass = fullHeight
    ? 'overflow-hidden relative'  // edge-to-edge in the supplemental column
    : 'rounded-lg overflow-hidden border border-stone-200 relative';

  if (offline || !loadState.ready) {
    return (
      <div
        className={`${containerClass} bg-stone-50 flex items-center justify-center`}
        style={containerStyle}
        data-testid={!GOOGLE_MAPS_API_KEY ? 'community-map-no-key' : 'community-map-unavailable'}
      >
        <div className="text-center text-xs text-stone-500 p-3">
          <span role="status">{offline ? 'Google Maps is unavailable in desktop offline mode.' : loadState.error?.message || 'Loading community map...'}</span>
          {!offline && loadState.error?.retryable && <button type="button" className="block mx-auto mt-2 underline" onClick={retryLoading} data-testid="community-map-retry">Try again</button>}
        </div>
      </div>
    );
  }

  const viable = addresses.filter((a) => a.place_id);
  const existingPlaceIds = new Set(viable.map((a) => a.place_id));

  return (
    <div
      className={containerClass}
      style={containerStyle}
      data-testid="community-map"
      // Suppress the browser's default context menu so right-clicks on
      // markers feel like a deliberate UI gesture, not an OS pop-up.
      onContextMenu={(e) => e.preventDefault()}
    >
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={LAS_VEGAS}
          defaultZoom={viable.length ? 12 : 11}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          mapTypeControl
          // Top-left holds the click hint overlay; place the Map/Satellite
          // toggle at top-center so it stays clear of it.
          mapTypeControlOptions={{ position: ControlPosition.TOP_CENTER }}
          clickableIcons={false}
          style={{ width: '100%', height: '100%' }}
        >
          <MarkerLayer
            key={`${selectionKey}:${JSON.stringify(viable.map(a => [a.place_id, a.lat, a.lng, a.formatted_address]))}`}
            addresses={viable}
            pendingPins={pendingPins}
            onSavedAddressRemove={onSavedAddressRemove}
            onPendingPinRemove={onPendingPinRemove}
            fitKey={fitKey}
            captureSelection={captureSelection}
            isCurrentSelection={isCurrentSelection}
          />
          {onPendingPinAdd && (
            <ClickHandler
              key={selectionKey}
              onPendingPinAdd={onPendingPinAdd}
              existingPlaceIds={existingPlaceIds}
              captureSelection={captureSelection}
              isCurrentSelection={isCurrentSelection}
            />
          )}
        </Map>
      </APIProvider>

      {/* Hint overlay while pending-pin picker is active and empty */}
      {onPendingPinAdd && pendingPins.length === 0 && (
        <div
          className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm border border-stone-200 rounded-md px-3 py-1.5 text-[11px] font-medium text-stone-600 shadow-sm pointer-events-none space-y-0.5"
          data-testid="community-map-click-hint"
        >
          <div>Click buildings on the map to stage addresses</div>
          <div className="text-[10px] text-stone-400">Right-click any pin to remove it</div>
        </div>
      )}
    </div>
  );
}
